import type { AssistantTaskListItem } from './assistantTaskLaneState';

const TERMINAL_TASK_STATES = new Set(['completed', 'failed', 'cancelled', 'expired']);

const toAnnouncementTaskKey = (
  task: Pick<AssistantTaskListItem, 'id' | 'runId' | 'workflowKey'>,
): string => `${task.id}::${task.runId ?? ''}::${task.workflowKey ?? ''}`;

const compareAnnouncementTaskFreshness = (
  left: Pick<AssistantTaskListItem, 'updatedAt' | 'state' | 'progressPercent'>,
  right: Pick<AssistantTaskListItem, 'updatedAt' | 'state' | 'progressPercent'>,
): number => {
  const leftUpdatedAt = Number(left.updatedAt ?? 0);
  const rightUpdatedAt = Number(right.updatedAt ?? 0);
  if (leftUpdatedAt !== rightUpdatedAt) {
    return leftUpdatedAt - rightUpdatedAt;
  }

  const leftTerminal = TERMINAL_TASK_STATES.has(String(left.state ?? '').trim().toLowerCase()) ? 1 : 0;
  const rightTerminal = TERMINAL_TASK_STATES.has(String(right.state ?? '').trim().toLowerCase()) ? 1 : 0;
  if (leftTerminal !== rightTerminal) {
    return leftTerminal - rightTerminal;
  }

  const leftProgress = Number(left.progressPercent ?? -1);
  const rightProgress = Number(right.progressPercent ?? -1);
  if (leftProgress !== rightProgress) {
    return leftProgress - rightProgress;
  }

  return 0;
};

export const isConversationAnnouncementTaskTerminal = (
  task: Pick<AssistantTaskListItem, 'state'>,
): boolean => TERMINAL_TASK_STATES.has(String(task.state ?? '').trim().toLowerCase());

export const selectConversationAnnouncementTaskCandidates = (
  tasks: readonly AssistantTaskListItem[],
): AssistantTaskListItem[] => {
  const freshestTaskByKey = new Map<string, AssistantTaskListItem>();

  tasks.forEach((task) => {
    const key = toAnnouncementTaskKey(task);
    const current = freshestTaskByKey.get(key);
    if (!current || compareAnnouncementTaskFreshness(task, current) > 0) {
      freshestTaskByKey.set(key, task);
    }
  });

  return [...freshestTaskByKey.values()].sort((left, right) => {
    const leftUpdatedAt = Number(left.updatedAt ?? left.launchedAt ?? 0);
    const rightUpdatedAt = Number(right.updatedAt ?? right.launchedAt ?? 0);
    if (leftUpdatedAt !== rightUpdatedAt) {
      return leftUpdatedAt - rightUpdatedAt;
    }
    return left.title.localeCompare(right.title);
  });
};
